import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { api } from '../../api';
import Select from '../../components/Select';
import Field, { FormRow } from '../../components/ui/Field';
import Metrica, { MetricaGrid } from '../../components/ui/Metrica';
import EmptyState from '../../components/ui/EmptyState';
import DonutChart from '../../components/ui/DonutChart';
import BarChart from '../../components/ui/BarChart';
import { fmt, fmtInt, fmtData, fmtDataHora, hoje } from '../../utils/format';
import {
  labelUnidadePlural, temPeso, descreverArea, unidadeArea, KG_POR_SACO,
} from '../../utils/unidades';
import { CORES_GRAFICO } from '../../utils/graficos';

const TIPOS = [
  { value: 'periodo', label: 'Produção por período' },
  { value: 'armazem', label: 'Produção por armazém' },
  { value: 'safra', label: 'Produção por safra' },
  { value: 'resumo', label: 'Resumo por área de cultivo' },
];

/** Uma carga expandida, com todos os campos preenchidos. */
function LinhaCarga({ carga, aberta, onToggle, mostrarArmazem }) {
  const c = carga;
  const qtd = c.quantidade ?? c.pesoLiquidoKg;
  const detalhes = [
    ['Data/Hora', fmtDataHora(c.dataHoraEntrega)],
    ['Safra', c.safraLavoura?.safra?.descricao],
    ['Área de Cultivo', c.safraLavoura?.lavoura?.descricao],
    c.armazem && ['Armazém', `${c.armazem.nome} — ${c.armazem.cidade}`],
    c.pesoBrutoKg && ['Peso Bruto', `${fmt(c.pesoBrutoKg)} kg`],
    c.pesoLiquidoKg && ['Peso Líquido', `${fmt(c.pesoLiquidoKg)} kg`],
    c.taraKg && ['Tara', `${fmt(c.taraKg)} kg`],
    c.quantidade && c.unidadeProducao !== 'KG' &&
      ['Quantidade', `${fmt(c.quantidade)} ${labelUnidadePlural(c.unidadeProducao)}`],
    c.classificacao && [c.tipoClassificacao || 'Classificação', c.classificacao],
    c.umidadePct && ['Umidade', `${c.umidadePct}%`],
    c.impurezaPct && ['Impureza', `${c.impurezaPct}%`],
    c.placaCaminhao && ['Placa', c.placaCaminhao],
    c.depositadoEm && ['Depositado em nome de', c.depositadoEm],
    ['Nota fiscal', c.notaEmitida ? 'Emitida' : 'Não emitida'],
  ].filter(Boolean);

  return (
    <div className="expansivel">
      <button
        type="button"
        className="expansivel-cabecalho"
        onClick={onToggle}
        aria-expanded={aberta}
      >
        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', minWidth: '70px' }}>
          {fmtData(c.dataHoraEntrega)}
        </span>
        <span style={{ flex: 1, fontWeight: 600, minWidth: '110px' }}>
          {c.safraLavoura?.lavoura?.tipoArea === 'ESTUFA' ? 'Estufa' : 'Lavoura'}
          {' — '}{c.safraLavoura?.lavoura?.descricao}
        </span>
        <span>
          <strong>{fmt(qtd)}</strong>{' '}
          <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
            {labelUnidadePlural(c.unidadeProducao || 'KG')}
          </span>
        </span>
        {mostrarArmazem && c.armazem && (
          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{c.armazem.nome}</span>
        )}
        <span className="expansivel-seta" aria-hidden="true">{aberta ? '▲' : '▼'}</span>
      </button>

      {aberta && (
        <div className="expansivel-corpo">
          <div className="detalhes-grid">
            {detalhes.map(([rotulo, valor]) => (
              <div key={rotulo}>
                <span className="detalhe-rotulo">{rotulo}</span>
                <span className="detalhe-valor">{valor || '—'}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Gráficos de produção que aparecem nos relatórios quando há cargas.
 * Mostra: produção por lavoura (barras) + classificação (donut) + umidade/impureza.
 */
function GraficosProducao({ cargas }) {
  // ── Produção por lavoura ──
  const porLavoura = useMemo(() => {
    const acc = {};
    cargas.forEach(c => {
      const nome = c.safraLavoura?.lavoura?.descricao || 'Sem área';
      const qtd = c.quantidade || c.pesoLiquidoKg || 0;
      acc[nome] = (acc[nome] || 0) + qtd;
    });
    return Object.entries(acc)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value);
  }, [cargas]);

  // ── Classificação (tipoClassificacao + classificacao) ──
  const porClassificacao = useMemo(() => {
    const comClassificacao = cargas.filter(c => c.classificacao);
    if (comClassificacao.length < 2) return null;
    const acc = {};
    comClassificacao.forEach(c => {
      const label = c.tipoClassificacao
        ? `${c.tipoClassificacao}: ${c.classificacao}`
        : c.classificacao;
      acc[label] = (acc[label] || 0) + 1;
    });
    return Object.entries(acc)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value);
  }, [cargas]);

  // ── Título da classificação ──
  const tipoClassTitulo = useMemo(() => {
    const tipos = [...new Set(cargas.filter(c => c.tipoClassificacao).map(c => c.tipoClassificacao))];
    if (tipos.length === 1) return `Distribuição por ${tipos[0]}`;
    return 'Distribuição por Classificação';
  }, [cargas]);

  // ── Umidade média por lavoura ──
  const umidadePorLavoura = useMemo(() => {
    const acc = {};
    cargas.forEach(c => {
      if (c.umidadePct == null) return;
      const nome = c.safraLavoura?.lavoura?.descricao || 'Sem área';
      if (!acc[nome]) acc[nome] = { soma: 0, count: 0 };
      acc[nome].soma += c.umidadePct;
      acc[nome].count += 1;
    });
    const result = Object.entries(acc)
      .map(([label, d]) => ({ label, value: d.soma / d.count }))
      .sort((a, b) => b.value - a.value);
    return result.length >= 1 ? result : null;
  }, [cargas]);

  // ── Impureza média por lavoura ──
  const impurezaPorLavoura = useMemo(() => {
    const acc = {};
    cargas.forEach(c => {
      if (c.impurezaPct == null) return;
      const nome = c.safraLavoura?.lavoura?.descricao || 'Sem área';
      if (!acc[nome]) acc[nome] = { soma: 0, count: 0 };
      acc[nome].soma += c.impurezaPct;
      acc[nome].count += 1;
    });
    const result = Object.entries(acc)
      .map(([label, d]) => ({ label, value: d.soma / d.count }))
      .sort((a, b) => b.value - a.value);
    return result.length >= 1 ? result : null;
  }, [cargas]);

  if (porLavoura.length < 2 && !porClassificacao && !umidadePorLavoura && !impurezaPorLavoura) return null;

  // Unidade predominante para rótulo do eixo
  const unidades = {};
  cargas.forEach(c => { const u = c.unidadeProducao || 'KG'; unidades[u] = (unidades[u] || 0) + 1; });
  const unidadePrincipal = Object.entries(unidades).sort((a, b) => b[1] - a[1])[0]?.[0] || 'KG';

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      {(porLavoura.length >= 2 || porClassificacao) && (
        <div className="grade-graficos">
          {porLavoura.length >= 2 && (
            <div>
              <h3 style={{ fontSize: '0.82rem', marginBottom: '0.6rem' }}>Produção por área de cultivo</h3>
              <BarChart
                data={porLavoura.map((item, i) => ({
                  label: item.label,
                  value: item.value,
                  color: CORES_GRAFICO[i % CORES_GRAFICO.length],
                }))}
                formatValue={(v) => `${fmt(v)} ${labelUnidadePlural(unidadePrincipal)}`}
                height={26}
                labelWidth={100}
              />
            </div>
          )}

          {porClassificacao && (
            <div>
              <h3 style={{ fontSize: '0.82rem', marginBottom: '0.6rem' }}>{tipoClassTitulo}</h3>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <DonutChart
                  data={porClassificacao.map((c, i) => ({
                    label: c.label,
                    value: c.value,
                    color: CORES_GRAFICO[i % CORES_GRAFICO.length],
                  }))}
                  centro={`${cargas.filter(c => c.classificacao).length}`}
                  centroSub="registros"
                  size={160}
                  thickness={28}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {(umidadePorLavoura || impurezaPorLavoura) && (
        <div className="grade-graficos">
          {umidadePorLavoura && (
            <div>
              <h3 style={{ fontSize: '0.82rem', marginBottom: '0.6rem' }}>Umidade média (%)</h3>
              <BarChart
                data={umidadePorLavoura.map((item) => ({
                  label: item.label,
                  value: item.value,
                  color: 'var(--info)',
                }))}
                formatValue={(v) => `${v.toFixed(1)}%`}
                height={22}
                labelWidth={90}
              />
            </div>
          )}
          {impurezaPorLavoura && (
            <div>
              <h3 style={{ fontSize: '0.82rem', marginBottom: '0.6rem' }}>Impureza média (%)</h3>
              <BarChart
                data={impurezaPorLavoura.map((item) => ({
                  label: item.label,
                  value: item.value,
                  color: 'var(--danger)',
                }))}
                formatValue={(v) => `${v.toFixed(2)}%`}
                height={22}
                labelWidth={90}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Aba de relatórios de produção.
 *
 * Estava embutida em Cargas.js junto com o formulário e a listagem, num arquivo
 * de 620 linhas com vinte estados no mesmo componente. Separado aqui, o estado
 * dos filtros de relatório não provoca mais re-render do formulário.
 */
export default function RelatoriosProducao({ armazens, safras, mostrarTitulo = true }) {
  const [tipo, setTipo] = useState('');
  const [armazemId, setArmazemId] = useState('');
  const [inicio, setInicio] = useState('');
  const [fim, setFim] = useState('');
  const [safraId, setSafraId] = useState('');
  const [safraLavouraId, setSafraLavouraId] = useState('');
  const [safraLavouras, setSafraLavouras] = useState([]);
  const [cargas, setCargas] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [expandida, setExpandida] = useState(null);
  const [filtroCultura, setFiltroCultura] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [erro, setErro] = useState('');
  const [erroAreas, setErroAreas] = useState('');
  const [carregandoAreas, setCarregandoAreas] = useState(false);
  const [buscou, setBuscou] = useState(false);

  // Conta as buscas para descartar resposta de filtro que o usuário já trocou:
  // sem isso a lista antiga aparecia sob o filtro novo.
  const buscaRef = useRef(0);

  const trocarTipo = (t) => {
    buscaRef.current += 1;
    setTipo(t);
    setCargas([]); setResumo(null); setBuscou(false); setErro(''); setBuscando(false);
    setArmazemId(''); setInicio(''); setFim('');
    setSafraId(''); setSafraLavouraId(''); setFiltroCultura('');
  };

  /**
   * Áreas plantadas da safra escolhida.
   *
   * Trocar de safra rápido fazia a resposta antiga chegar depois da nova, e o
   * combo listava as áreas da safra anterior — o produtor consultava produção da
   * área errada. A guarda `vivo` descarta a resposta que ficou para trás.
   */
  const carregarAreas = useCallback(() => {
    if (!safraId) {
      setSafraLavouras([]); setSafraLavouraId(''); setErroAreas(''); setCarregandoAreas(false);
      return undefined;
    }
    let vivo = true;
    setCarregandoAreas(true);
    setErroAreas('');
    api.getLavourasDaSafra(safraId)
      .then(l => { if (vivo) setSafraLavouras(l || []); })
      .catch(() => {
        if (!vivo) return;
        setSafraLavouras([]);
        setErroAreas('Não conseguimos buscar as áreas desta safra agora. Confira a internet e tente de novo.');
      })
      .finally(() => { if (vivo) setCarregandoAreas(false); });
    return () => { vivo = false; };
  }, [safraId]);

  useEffect(() => carregarAreas(), [carregarAreas]);

  // Trocar a safra invalida a área já escolhida: ela pertence à safra anterior.
  const trocarSafra = (v) => { setSafraId(v); setSafraLavouraId(''); };

  /** Executa a busca do modo ativo, com estado de carregamento e erro visíveis. */
  const buscar = async () => {
    const minhaBusca = ++buscaRef.current;
    const atual = () => buscaRef.current === minhaBusca;
    setBuscando(true);
    setErro('');
    try {
      let cargasNovas = [];
      let resumoNovo = null;
      if (tipo === 'periodo') {
        cargasNovas = await api.getCargasPorPeriodo(`${inicio}T00:00:00`, `${fim}T23:59:59`);
      } else if (tipo === 'armazem') {
        cargasNovas = await api.getCargasPorArmazem(armazemId);
      } else if (tipo === 'safra') {
        cargasNovas = await api.getCargasPorSafra(safraId);
      } else if (tipo === 'resumo') {
        resumoNovo = await api.getResumoSafraLavoura(safraLavouraId);
      }
      if (!atual()) return;
      setCargas(cargasNovas || []);
      setResumo(resumoNovo);
      setBuscou(true);
    } catch (e) {
      if (!atual()) return;
      setErro(e.message || 'Não conseguimos carregar o relatório agora. Confira a internet e tente de novo.');
      setCargas([]); setResumo(null);
    } finally {
      if (atual()) setBuscando(false);
    }
  };

  // Cada modo tem seu próprio requisito mínimo; sem isso o botão dispara uma
  // busca que o backend rejeita.
  const podeBuscar = {
    periodo: !!inicio && !!fim && inicio <= fim,
    armazem: !!armazemId,
    safra: !!safraId,
    resumo: !!safraLavouraId,
  }[tipo];

  const culturas = useMemo(
    () => [...new Set(cargas.map(c => c.safraLavoura?.safra?.cultura || c.safraLavoura?.tipoGrao).filter(Boolean))],
    [cargas]
  );

  const filtradas = useMemo(
    () => (filtroCultura
      ? cargas.filter(c => (c.safraLavoura?.safra?.cultura || c.safraLavoura?.tipoGrao) === filtroCultura)
      : cargas),
    [cargas, filtroCultura]
  );

  // Quantidade física só soma dentro da mesma unidade.
  const porUnidade = useMemo(() => {
    const acc = {};
    filtradas.forEach(c => {
      const u = c.unidadeProducao || 'KG';
      const q = c.quantidade || c.pesoLiquidoKg || 0;
      acc[u] = acc[u] || { total: 0, count: 0 };
      acc[u].total += q;
      acc[u].count += 1;
    });
    return acc;
  }, [filtradas]);

  const unidades = Object.keys(porUnidade);
  const mostrarArmazem = cargas.some(c => c.armazem);

  const botaoBuscar = (
    <button type="button" className="btn-secondary" onClick={buscar} disabled={!podeBuscar || buscando}>
      {buscando ? 'Buscando...' : 'Buscar'}
    </button>
  );

  const opcoesSafra = safras.map(s => ({ value: s.id, label: `${s.cultura || 'Safra'} — ${s.descricao}` }));

  return (
    <div className="card">
      {mostrarTitulo && (
        <div className="secao-titulo">
          <h2>Relatórios de Produção</h2>
        </div>
      )}

      <FormRow>
        <Field label="Tipo de relatório" largura="cheia">
          <Select value={tipo} onChange={trocarTipo} options={TIPOS} placeholder="Selecione o tipo" />
        </Field>
      </FormRow>

      {tipo === 'periodo' && (
        <div className="filtros">
          <Field label="Início">
            <input type="date" value={inicio} max={fim || hoje()} onChange={e => setInicio(e.target.value)} />
          </Field>
          <Field label="Fim" erro={inicio && fim && inicio > fim ? 'O fim deve ser depois do início.' : undefined}>
            <input type="date" value={fim} min={inicio || undefined} onChange={e => setFim(e.target.value)} />
          </Field>
          {botaoBuscar}
        </div>
      )}

      {tipo === 'armazem' && (
        <div className="filtros">
          <Field label="Armazém">
            <Select
              value={armazemId}
              onChange={setArmazemId}
              options={[{ value: '0', label: 'Não informado' }, ...armazens.map(a => ({ value: a.id, label: a.nome }))]}
              placeholder="Selecione"
            />
          </Field>
          {botaoBuscar}
        </div>
      )}

      {tipo === 'safra' && (
        <div className="filtros">
          <Field label="Safra / Ciclo">
            <Select value={safraId} onChange={trocarSafra} options={opcoesSafra} placeholder="Selecione" />
          </Field>
          {botaoBuscar}
        </div>
      )}

      {tipo === 'resumo' && (
        <>
          <div className="filtros">
            <Field label="Safra / Ciclo">
              <Select value={safraId} onChange={trocarSafra} options={opcoesSafra} placeholder="Selecione" />
            </Field>
            <Field
              label="Área de Cultivo"
              hint={safraId && !carregandoAreas && !erroAreas && safraLavouras.length === 0
                ? 'Esta safra não tem área de cultivo cadastrada.'
                : undefined}
            >
              <Select
                value={safraLavouraId}
                onChange={setSafraLavouraId}
                options={safraLavouras.map(sl => ({ value: sl.id, label: descreverArea(sl.lavoura, sl.areaHectares) }))}
                placeholder={!safraId
                  ? 'Escolha a safra primeiro'
                  : carregandoAreas ? 'Buscando áreas...' : 'Selecione'}
                disabled={!safraId || carregandoAreas}
              />
            </Field>
            {botaoBuscar}
          </div>

          {/* Combo vazio por falha de rede não pode parecer safra sem área. */}
          {erroAreas && (
            <div className="aviso aviso--erro" role="alert">
              {erroAreas}{' '}
              <button type="button" onClick={carregarAreas}
                style={{ background: 'none', border: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer', padding: 0, minHeight: 'unset', fontWeight: 700 }}>
                Tentar novamente
              </button>
            </div>
          )}
        </>
      )}

      {erro && <div className="aviso aviso--erro" style={{ marginTop: '1rem' }} role="alert">{erro}</div>}

      {resumo && (() => {
        const unidade = resumo.unidadeProducao || 'KG';
        const rotulo = labelUnidadePlural(unidade);
        const estufa = resumo.tipoArea === 'ESTUFA';
        // Saca de 60 kg é medida de grão. Numa estufa, que mede em kg, caixa ou
        // bandeja, essa conversão não diz nada — e o total na unidade registrada
        // já aparece no card "Total em ...". O backend manda null nesses casos;
        // a checagem aqui é para não exibir número velho durante o deploy.
        const mostrarSacas = temPeso(unidade) && !estufa && resumo.totalSacos != null;
        return (
          <div style={{ marginTop: '1.25rem' }}>
            <MetricaGrid min={140}>
              <Metrica label="Total de registros" value={fmtInt(resumo.totalCargas)} />
              <Metrica label={`Total em ${rotulo}`} value={`${fmt(resumo.totalPesoKg)} ${rotulo}`} />
              <Metrica label="Média por registro" value={`${fmt(resumo.mediaPesoKg)} ${rotulo}`} />
              {mostrarSacas && (
                <Metrica label="Total em sacas" value={fmt(resumo.totalSacos)} dica="60 kg = 1 saca" />
              )}
              {!estufa && resumo.mediaSacosHectare != null && (
                <Metrica
                  label={`${temPeso(unidade) ? 'Sacas' : rotulo} / ${unidadeArea(resumo.tipoArea)}`}
                  value={fmt(resumo.mediaSacosHectare)}
                />
              )}
            </MetricaGrid>
          </div>
        );
      })()}

      {cargas.length > 0 && (
        <div style={{ marginTop: '1.25rem' }}>
          {culturas.length > 1 && (
            <div className="chips" style={{ marginBottom: '0.75rem' }}>
              <span className="chips-label">Cultura:</span>
              <button type="button" className={`chip ${!filtroCultura ? 'chip--ativo' : ''}`} onClick={() => setFiltroCultura('')}>
                Todas
              </button>
              {culturas.map(c => (
                <button
                  key={c}
                  type="button"
                  className={`chip ${filtroCultura === c ? 'chip--ativo' : ''}`}
                  onClick={() => setFiltroCultura(c)}
                >{c}</button>
              ))}
            </div>
          )}

          {/* ── Gráficos de produção ── */}
          <GraficosProducao cargas={filtradas} />

          <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.75rem' }}>
            {filtradas.length} registro{filtradas.length === 1 ? '' : 's'} encontrado{filtradas.length === 1 ? '' : 's'}
          </p>

          {unidades.length > 1 && (
            <div className="aviso aviso--atencao">
              Há registros em unidades diferentes. Como não é possível somar caixas com quilos,
              os totais aparecem separados por unidade no fim da lista.
            </div>
          )}

          {filtradas.map(c => (
            <LinhaCarga
              key={c.id}
              carga={c}
              aberta={expandida === c.id}
              onToggle={() => setExpandida(expandida === c.id ? null : c.id)}
              mostrarArmazem={mostrarArmazem}
            />
          ))}

          <div style={{ paddingTop: '0.75rem', borderTop: '1px solid var(--border)', fontSize: '0.85rem' }}>
            {unidades.map(u => (
              <div key={u} style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '0.3rem' }}>
                <span>
                  <span style={{ color: 'var(--text-muted)' }}>Total {labelUnidadePlural(u)}:</span>{' '}
                  <strong>{fmt(porUnidade[u].total)}</strong>{' '}
                  <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                    ({porUnidade[u].count} registro{porUnidade[u].count === 1 ? '' : 's'})
                  </span>
                </span>
                {u === 'KG' && (
                  <span>
                    <span style={{ color: 'var(--text-muted)' }}>Equivale a:</span>{' '}
                    <strong>{fmt(porUnidade[u].total / KG_POR_SACO)}</strong> sacas
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {buscou && !erro && cargas.length === 0 && !resumo && (
        <EmptyState
          compacto
          icone="busca"
          titulo="Nenhum registro no filtro escolhido"
          descricao="Ajuste o período, a safra ou o armazém e busque de novo."
        />
      )}

      {!tipo && (
        <EmptyState
          compacto
          icone="resultado"
          titulo="Escolha um tipo de relatório"
          descricao="Você pode ver a produção por período, por armazém, por safra, ou o resumo de uma área de cultivo."
        />
      )}
    </div>
  );
}
